<template>
  <label
    class="relative h-12 w-full flex b rounded-xs duration focus-within:outline-auto"
    :class="[
      disabled
        ? 'bg-grey-90 b-grey-50 c-grey-30'
        : invalid
          ? (variant ? 'b-red-60' : 'b-red-30')
          : 'b-grey-40 hover:b-grey-10',
      { 'bg-white cursor-pointer focus-within:b-grey-10': !disabled },
    ]"
    data-test-id="vf-select"
  >
    <span
      class="pointer-events-none absolute-0 ml-3 mr-2 mt-1 form-label"
      :class="[!disabled && (invalid ? 'c-red-30' : 'c-grey-20'), labelClass]"
      data-test-id="vf-select-label"
    >
      <!-- @slot Slot is meant for `label` only -->
      <slot />
      <span v-if="required" aria-hidden="true"> *</span>
    </span>
    <base-select
      v-bind="{ options, placeholder, disabled, required }"
      v-model="modelValue"
      class="full pb-1 pl-3 pr-10 pt-5 form-input"
      :class="[
        !disabled && (modelValue ? 'c-grey-10' : 'c-grey-20'),
        textClass,
      ]"
      @update:model-value="$emit('change', $event)"
    />
    <vf-icon
      class="pointer-events-none absolute inset-y-0 right-0 my-a mr-2 "
      :class="[disabled ? 'c-grey-50' : 'c-grey-10', iconClass]"
      dir="down"
      name="chevron"
      :size="iconSize || fallbackIconSize"
    />
  </label>
</template>

<script lang="ts" setup>
import type { CSSClass } from '#types/common'
import type { SelectOption } from '#types/components/base/select'
import type { IconSizes } from '#types/sizes'

defineProps<{
  /**
   * Defines list of options
   */
  options: (string | SelectOption)[] | (() => Promise<SelectOption[]>)
  /**
   * Defines placeholder when no selection
   */
  placeholder?: string
  /**
   * Defines disabled state
   */
  disabled?: boolean
  /**
   * Defines invalid state
   */
  invalid?: boolean
  /**
   * Defines if the select is required for accessibility purposes
   */
  required?: boolean
  /**
   * Defines variant
   */
  variant?: 'inverse'
  /**
   * Defines class the needed to be added to icon
   */
  iconClass?: CSSClass

  /**
   * Defines icon size
   */
  iconSize?: IconSizes
  /**
   * Defines class the needed to be added to text and base select
   */
  textClass?: CSSClass
  /**
   * Defines class the needed to be added to label and base select
   */
  labelClass?: CSSClass
}>()

defineEmits<{ change: [value?: string | number | object] }>()

const modelValue = defineModel<string | number | object>()

const { iconSize: fallbackIconSize } = useAppConfig().components.vf.select
</script>
